'use client'

import { FC, memo, ReactNode, useEffect, useReducer, useRef } from 'react'
import s from './DailyCarousel.module.sass'
import { SectionCarousel } from '@shared/ui/Layout/SectionCarousel'
import { DailyCard, DailyCardSkeleton } from '@widgets/Common/Daily/DailyCard'
import { DAILY_SKELETON_KEYS, mapDailyArticleToCardProps } from './model/dailyArticlesView'
import { useDailyArticlesSwr } from './hooks/useDailyArticlesSwr'
import { readDailyArticlesCache } from '@shared/lib/browser-cache/dailyArticlesCache'
import { useOnScreen } from '@shared/lib/hooks/useOnScreen'

interface Props {
  container?: React.ComponentType<{ children: ReactNode; classNameContainer?: string }>
  className?: string
}

const DefaultContainer: FC<{ children: ReactNode; classNameContainer?: string }> = ({
  children,
  classNameContainer,
}) => <div className={classNameContainer}>{children}</div>

const onScreenOptions = { threshold: 0, rootMargin: '400px 0px 400px 0px' } as const
const lacksIntersectionObserver = typeof IntersectionObserver === 'undefined'
const mergeLoadSignal = (enabled: boolean, signal: boolean): boolean => enabled || signal

const renderDailyCardSkeleton = (key: (typeof DAILY_SKELETON_KEYS)[number]) => <DailyCardSkeleton key={key} />

const DailyCarousel: FC<Props> = ({ container: ContainerComponent = DefaultContainer, className = '' }) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [shouldLoad, applyLoadSignal] = useReducer(mergeLoadSignal, !!readDailyArticlesCache()?.length)
  const isNearViewport = useOnScreen({ ref: rootRef, optionsProps: onScreenOptions })

  useEffect(() => applyLoadSignal(isNearViewport || lacksIntersectionObserver), [isNearViewport])

  const { articles, isVisible, showSkeleton } = useDailyArticlesSwr({ loadEnabled: shouldLoad })

  return (
    <div ref={rootRef} className={s.root}>
      {isVisible ? (
        <ContainerComponent classNameContainer={className}>
          <SectionCarousel
            title={'Афиша Daily'}
            link={'https://daily.afisha.ru/'}
            skeleton={showSkeleton}
            className={s.carousel}>
            <SectionCarousel.Skeletons>{DAILY_SKELETON_KEYS.map(renderDailyCardSkeleton)}</SectionCarousel.Skeletons>
            <SectionCarousel.Items key={articles?.length ?? 0}>
              {articles?.map((article) => (
                <DailyCard key={article.id} {...mapDailyArticleToCardProps(article)} />
              ))}
            </SectionCarousel.Items>
          </SectionCarousel>
        </ContainerComponent>
      ) : null}
    </div>
  )
}

export default memo(DailyCarousel)
